import { zodResolver } from '@hookform/resolvers/zod';
import { usePostHog } from 'posthog-js/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useLogin } from '@/auth/useLogin';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

export const SignIn = () => {
  const showUsernameForm = new URLSearchParams(window.location.search).get('u') === '1';

  return (
    <div className="h-screen flex justify-center items-center bg-neutral-800">
      <div>{showUsernameForm ? <LoginWithUsername /> : <OtpForm />}</div>
    </div>
  );
};

const usernameStrictSchema = z.object({
  password: z.string(),
  username: z.string(),
});
type UsernameForm = z.output<typeof usernameStrictSchema>;

//
// LoginWithUsername
//
const LoginWithUsername = () => {
  const posthog = usePostHog();

  const form = useForm({
    defaultValues: { password: '', username: '' },
    resolver: zodResolver(usernameStrictSchema),
  });

  const { userVisibleError, signInWithPassword } = useLogin();

  const onValidSubmit = async ({ password, username }: UsernameForm) => {
    try {
      await signInWithPassword(username, password);
      posthog?.capture('clicked_sign_in', {
        signup_method: 'username',
      });
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <Card className="w-[320px]">
      <CardHeader>
        <CardTitle className="text-xl">Login</CardTitle>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            className={'grid gap-6'}
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
          >
            {/* Username */}
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input {...field} required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="on" required type="password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ActionButton
              disabled={form.formState.isSubmitting}
              loading={form.formState.isSubmitting}
              type="submit"
            >
              Sign in
            </ActionButton>
          </form>
        </FormWrapper>
      </CardContent>

      {userVisibleError && (
        <CardFooter>
          <div className={'text-red-500 dark:text-red-400 font-medium'}>{userVisibleError}</div>
        </CardFooter>
      )}
    </Card>
  );
};

//
// OtpForm
//

const OtpForm = () => {
  const {
    mobile,
    code,
    isLoading,
    showCodeInput,
    userVisibleError,
    handleOnMobileChange,
    handleCodeChange,
    sendCode,
    verifyCode,
  } = useLogin();
  const posthog = usePostHog();

  const handleSignIn = async () => {
    posthog?.capture('clicked_sign_in', {
      signup_method: 'OTP',
    });
    await verifyCode(code);
  };

  return (
    <Card className="w-[320px]">
      <CardHeader>
        <CardTitle className="text-xl">Login</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6">
        {/* Mobile */}
        {!showCodeInput && (
          <>
            <div className="grid gap-2">
              <Label>Mobile number</Label>
              <Input
                disabled={showCodeInput}
                maxLength={10}
                onChange={(e) => handleOnMobileChange(e.target.value)}
                required
                value={mobile}
              />
            </div>

            <ActionButton
              disabled={isLoading || showCodeInput}
              loading={isLoading}
              onClick={() => sendCode(mobile)}
            >
              Send verification code
            </ActionButton>
          </>
        )}

        {/* Code */}
        {showCodeInput && (
          <>
            <div className="grid gap-2">
              <Label>Code</Label>
              <InputOTP
                autoFocus={true}
                maxLength={6}
                onChange={(value) => handleCodeChange(value)}
                value={code}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <ActionButton disabled={isLoading} loading={isLoading} onClick={handleSignIn}>
              Sign in
            </ActionButton>
          </>
        )}
      </CardContent>
      <CardFooter>
        <div className={'text-red-500 dark:text-red-400'}>{userVisibleError}</div>
      </CardFooter>
    </Card>
  );
};
